import React, { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const XP_SOURCES = [
  'DAILY_TASK_CLAIM',
  'STREAK_MILESTONE',
  'LEVEL_UP',
  'ADMIN_GRANT',
];

interface XpTransactionRow {
  id: string;
  userId: string;
  amount: number;
  source: string;
  description?: string | null;
  balanceAfter: number;
  createdAt: string;
  user: {
    id: string;
    email?: string | null;
    phone?: string | null;
    fullName?: string | null;
  };
}

export const XpTransactionList: React.FC = () => {
  const [userIdFilter, setUserIdFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | undefined>();

  const { tableProps } = useTable<XpTransactionRow>({
    resource: 'xp-transactions',
    filters: {
      permanent: [
        ...(userIdFilter
          ? [{ field: 'userId', operator: 'eq' as const, value: userIdFilter }]
          : []),
        ...(sourceFilter
          ? [{ field: 'source', operator: 'eq' as const, value: sourceFilter }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List title="XP Ledger">
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Filter by user ID"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value.trim())}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="Source"
          allowClear
          style={{ width: 200 }}
          value={sourceFilter}
          onChange={setSourceFilter}
          options={XP_SOURCES.map((s) => ({ value: s, label: s }))}
        />
      </Space>

      <Table {...tableProps} rowKey="id" scroll={{ x: 960 }}>
        <Table.Column
          title="Time"
          dataIndex="createdAt"
          width={160}
          render={(v: string) => dayjs(v).format('YYYY-MM-DD HH:mm')}
        />
        <Table.Column
          title="User"
          width={220}
          render={(_: unknown, row: XpTransactionRow) => (
            <div>
              <div style={{ fontWeight: 500 }}>{row.user.fullName || '—'}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.user.email || row.user.phone || row.userId}
              </Text>
            </div>
          )}
        />
        <Table.Column
          title="Amount"
          dataIndex="amount"
          width={100}
          render={(amount: number) => (
            <Text style={{ color: amount >= 0 ? '#3ecf8e' : '#ff4d4f', fontWeight: 600 }}>
              {amount >= 0 ? `+${amount}` : amount}
            </Text>
          )}
        />
        <Table.Column
          title="Source"
          dataIndex="source"
          width={160}
          render={(source: string) => <Tag>{source}</Tag>}
        />
        <Table.Column title="Description" dataIndex="description" ellipsis />
        <Table.Column title="Balance after" dataIndex="balanceAfter" width={120} />
      </Table>
    </List>
  );
};
